import type { ProcessedAnnotation } from './rooftopAnnotations'

export type RiskLevel = 'Low' | 'Moderate' | 'High' | 'Critical'

export interface SectionImpactData {
  inspectionAreaId: string
  inspectionAreaLabel: string
  totalArea: number
  impactedArea: number
  percentAffected: number
  annotationCount: number
  riskLevel: RiskLevel
}

export interface GroupedAnnotations {
  [inspectionAreaId: string]: ProcessedAnnotation[]
  unassigned: ProcessedAnnotation[]
}

export function convertInspectionAreaToSquareFeet(areaCm2: number): number {
  return areaCm2 / 929.03
}

export function calculateSectionImpactedArea(
  annotations: ProcessedAnnotation[],
  inspectionAreaId: string
): number {
  const sectionAnnotations = annotations.filter(
    (ann) => ann.tags?.inspectionArea?.value === inspectionAreaId
  )

  return sectionAnnotations.reduce((total, annotation) => {
    if (annotation.calculatedArea) {
      return total + annotation.calculatedArea
    }
    return total
  }, 0)
}

export function calculateSectionPercentAffected(
  impactedArea: number,
  totalSectionArea: number
): number {
  if (totalSectionArea === 0) return 0
  return (impactedArea / totalSectionArea) * 100
}

export function determineSectionRiskLevel(percentAffected: number): RiskLevel {
  if (percentAffected > 45) return 'Critical'
  if (percentAffected > 35) return 'High'
  if (percentAffected > 20) return 'Moderate'
  return 'Low'
}

export function getSectionRiskGuidance(riskLevel: RiskLevel): string {
  switch (riskLevel) {
    case 'Critical':
      return 'Roof system is at end of service life. Full replacement or major phased capital project required.'
    case 'High':
      return 'System is compromised. Prioritize phased tear off and replacement of affected sections.'
    case 'Moderate':
      return 'Core sampling recommended. Plan for phased remediation in targeted areas.'
    case 'Low':
      return 'Monitor with annual inspection. Spot repairs may be sufficient.'
  }
}

export function getPrimaryFailureMechanisms(riskLevel: RiskLevel): string {
  switch (riskLevel) {
    case 'Critical':
      return 'Widespread systemic saturation, poor drainage, curb and penetration failures, substrate deterioration'
    case 'High':
      return 'Large contiguous fields of saturation, slope-driven migration, flashing failures'
    case 'Moderate':
      return 'Expanding anomalies near edges or penetrations, limited field saturation'
    case 'Low':
      return 'Isolated anomalies, minor edge ingress, localized seam issues'
  }
}

export function groupAnnotationsBySection(
  annotations: ProcessedAnnotation[]
): GroupedAnnotations {
  const grouped: GroupedAnnotations = {
    unassigned: [],
  }

  annotations.forEach((annotation) => {
    const inspectionAreaId = annotation.tags?.inspectionArea?.value

    if (inspectionAreaId) {
      if (!grouped[inspectionAreaId]) {
        grouped[inspectionAreaId] = []
      }
      grouped[inspectionAreaId].push(annotation)
    } else {
      grouped.unassigned.push(annotation)
    }
  })

  return grouped
}

export function calculateTotalImpactedArea(
  annotations: ProcessedAnnotation[]
): number {
  return annotations.reduce((total, annotation) => {
    if (annotation.calculatedArea) {
      return total + annotation.calculatedArea
    }
    return total
  }, 0)
}

export function getRiskLevelColor(riskLevel: RiskLevel): string {
  switch (riskLevel) {
    case 'Critical':
      return '#dc2626'
    case 'High':
      return '#ea580c'
    case 'Moderate':
      return '#d97706'
    case 'Low':
      return '#4b5563'
  }
}

export function getRiskLevelBackgroundColor(riskLevel: RiskLevel): string {
  switch (riskLevel) {
    case 'Critical':
      return '#fef2f2'
    case 'High':
      return '#fff7ed'
    case 'Moderate':
      return '#fffbeb'
    case 'Low':
      return '#f8fafc'
  }
}

export function getSectionTopDamageTypes(
  annotations: ProcessedAnnotation[],
  inspectionAreaId: string,
  limit: number = 3
): string[] {
  const sectionAnnotations = annotations.filter(
    (ann) => ann.tags?.inspectionArea?.value === inspectionAreaId
  )

  const damageTypeCounts: Record<string, number> = {}
  sectionAnnotations.forEach((annotation) => {
    const types = annotation.damageTypes || []
    types.forEach((type) => {
      damageTypeCounts[type] = (damageTypeCounts[type] || 0) + 1
    })
  })

  return Object.entries(damageTypeCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([type]) => type)
}
